import React, { useState, useEffect, useMemo } from 'react';
import { Col, Row, Container, Form } from 'react-bootstrap';
import { useTranslation, Trans } from 'react-i18next';
import { useLocation, Link } from 'react-router-dom';

export default function FAQScreen() {
  const { t } = useTranslation();
  const location = useLocation();
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredFaqs, setFilteredFaqs] = useState([]);

  const faqs = useMemo(
    () => [
      {
        id: 'faq-1',
        question: t('faq-screen-question-1'),
        answers: [
          t('faq-screen-answer-1-row-1'),
          t('faq-screen-answer-1-row-2'),
        ],
      },
      {
        id: 'faq-2',
        question: t('faq-screen-question-2'),
        answers: [
          t('faq-screen-answer-2-row-1'),
          t('faq-screen-answer-2-row-2'),
        ],
      },
      {
        id: 'faq-5',
        question: t('faq-screen-question-5'),
        answers: [t('faq-screen-answer-5-row-1')],
      },
      {
        id: 'faq-4',
        question: t('faq-screen-question-4'),
        answers: [t('faq-screen-answer-4-row-1')],
      },
      {
        id: 'faq-15',
        question: t('faq-screen-question-15'),
        answers: [
          t('faq-screen-answer-15-row-1'),
          // t('faq-screen-answer-15-row-2'), // A hint to a list of pre-printed keycap colors (how certain hex colors look printed on the keycaps)
        ],
      },
      {
        id: 'faq-14',
        question: t('faq-screen-question-14'),
        answers: [t('faq-screen-answer-14-row-1')],
      },
      {
        id: 'faq-6',
        question: t('faq-screen-question-6'),
        answers: [
          t('faq-screen-answer-6-row-1'),
          t('faq-screen-answer-6-row-2'),
        ],
      },
      {
        id: 'faq-7',
        question: t('faq-screen-question-7'),
        answers: [t('faq-screen-answer-7-row-1')],
      },
      {
        id: 'faq-8',
        question: t('faq-screen-question-8'),
        answers: [t('faq-screen-answer-8-row-1')],
      },
      {
        id: 'faq-12',
        question: t('faq-screen-question-12'),
        answers: [
          t('faq-screen-answer-12-row-1'),
          t('faq-screen-answer-12-row-2'),
          t('faq-screen-answer-12-row-3'),
          t('faq-screen-answer-12-row-4'),
          t('faq-screen-answer-12-row-5'),
        ],
      },
      {
        id: 'faq-13',
        question: t('faq-screen-question-13'),
        answers: [t('faq-screen-answer-13-row-1')],
      },
      {
        id: 'faq-11',
        question: t('faq-screen-question-11'),
        answers: [t('faq-screen-answer-11-row-1')],
      },
      {
        id: 'faq-9',
        question: t('faq-screen-question-9'),
        answers: [t('faq-screen-answer-9-row-1')],
      },
      {
        id: 'faq-20',
        question: t('faq-screen-question-20'),
        answers: [
          <p key="answer-20">
            <Trans
              i18nKey="faq-screen-answer-20-row-1"
              components={{
                1: (
                  <Link
                    style={{ textDecoration: 'underline' }}
                    to="/shipping-costs"
                  />
                ),
              }}
            />
          </p>,
        ],
        searchableText: t('faq-screen-answer-20-row-1'), // needed, because the handleSearch function can't look through the the React elements in this answers array
      },
      {
        id: 'faq-16',
        question: t('faq-screen-question-16'),
        answers: [t('faq-screen-answer-16-row-1')],
      },
      {
        id: 'faq-17',
        question: t('faq-screen-question-17'),
        answers: [t('faq-screen-answer-17-row-1')],
      },
      {
        id: 'faq-18',
        question: t('faq-screen-question-18'),
        answers: [t('faq-screen-answer-18-row-1')],
      },
      {
        id: 'faq-19',
        question: t('faq-screen-question-19'),
        answers: [
          t('faq-screen-answer-19-row-1'),
          t('faq-screen-answer-19-row-2'),
        ],
      },
      {
        id: 'faq-3',
        question: t('faq-screen-question-3'),
        answers: [
          t('faq-screen-answer-3-row-1'),
          t('faq-screen-answer-3-row-2'),
          t('faq-screen-answer-3-row-3'),
        ],
      },
      /* Influencer Program:
        {
          id: 'faq-10',
          question: t('faq-screen-question-10'),
          answers: [t('faq-screen-answer-10-row-1')],
        },
      */
    ],
    [t]
  );

  useEffect(() => {
    setFilteredFaqs(faqs);
  }, [faqs]);

  useEffect(() => {
    const hash = location.hash.replace('#', '');
    if (hash) {
      const timeout = setTimeout(() => {
        scrollToElement(hash);
      }, 0); // Delay scroll to wait for DOM to render

      return () => clearTimeout(timeout);
    }
  }, [location, filteredFaqs]); // adding filteredFaqs to ensure the scroll waits until content is ready

  const scrollToElement = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView();
    }
  };

  const handleSearch = (e) => {
    setSearchTerm(e.target.value);
    const lowerCaseTerm = e.target.value.toLowerCase();

    if (!lowerCaseTerm) {
      setFilteredFaqs(faqs);
      return;
    }

    const filtered = faqs.filter((faq) => {
      const questionMatch = faq.question.toLowerCase().includes(lowerCaseTerm);

      const searchText = faq.searchableText || '';

      const answerMatch =
        searchText.toLowerCase().includes(lowerCaseTerm) ||
        faq.answers.some((answer) => {
          const text = typeof answer === 'string' ? answer : '';
          return text.toLowerCase().includes(lowerCaseTerm);
        });

      return questionMatch || answerMatch;
    });

    setFilteredFaqs(filtered);
  };

  return (
    <Container>
      <div className="container small-container">
        <title>{t('faq-screen-helmet')}</title>

        <h1 className="screen-heading">{t('faq-screen-heading')}</h1>

        <Form.Control
          type="text"
          placeholder={t('faq-screen-search-placeholder')}
          value={searchTerm}
          onChange={handleSearch}
          className="mobile-device-compatible"
        />

        <Row className="text-container">
          <Col>
            <section>
              {filteredFaqs.map((faq) => (
                <div key={faq.id} className="text-container-section">
                  <h4 id={faq.id}>{faq.question}</h4>
                  {faq.answers.map((answer, index) =>
                    React.isValidElement(answer) ? (
                      // If the value of answer is a <p></p> (a React element), clone it to add a key (if not already present)
                      // This is to ensure that I don't wrap a <p></p> inside another <p></p>
                      React.cloneElement(answer, { key: index })
                    ) : (
                      // Otherwise, wrap it in a paragraph
                      <p key={index}>{answer}</p>
                    )
                  )}
                </div>
              ))}
            </section>
          </Col>
        </Row>
      </div>
    </Container>
  );
}
